import React, { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
  User,
} from 'lucide-react';
import { register, sendVerificationCode } from '../services/api';

interface RegisterFormProps {
  onBackToLogin: () => void;
  onRegistered: (username: string) => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({
  onBackToLogin,
  onRegistered,
}) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const handleSendCode = async () => {
    if (!email.trim()) {
      setIsError(true);
      setMessage('请先填写邮箱');
      return;
    }

    setSendingCode(true);
    setMessage('');
    try {
      const result = await sendVerificationCode(email.trim());
      setIsError(!result.success);
      setMessage(result.message || (result.success ? '验证码已发送' : '发送失败'));
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : '验证码发送失败');
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setIsError(true);
      setMessage('两次输入的密码不一致');
      return;
    }

    setSubmitting(true);
    setMessage('');
    try {
      const result = await register({
        username: username.trim(),
        email: email.trim(),
        password,
        verification_code: verificationCode.trim(),
        invitation_code: invitationCode.trim(),
      });
      if (result.success) {
        setIsError(false);
        setMessage(result.message || '注册成功，请登录');
        onRegistered(username.trim());
      } else {
        setIsError(true);
        setMessage(result.message || '注册失败');
      }
    } catch (error) {
      setIsError(true);
      setMessage(error instanceof Error ? error.message : '注册失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7] p-4 font-sans flex items-center justify-center relative overflow-y-auto">
      <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-yellow-200/40 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-200/30 rounded-full blur-[120px]" />

      <div className="bg-white/90 backdrop-blur-3xl p-7 md:p-10 rounded-[2.5rem] shadow-xl w-full max-w-xl border border-white relative z-10 my-6">
        <button
          type="button"
          onClick={onBackToLogin}
          className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-black mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          返回登录
        </button>

        <div className="text-center mb-7">
          <div className="w-16 h-16 bg-[#FFE815] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-100">
            <KeyRound className="w-8 h-8 text-black" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900">邀请码注册</h2>
          <p className="text-sm text-gray-500 mt-2">邀请码由站长生成，成功注册后立即失效</p>
        </div>

        <form
          aria-label="邀请码注册"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          <label className="block">
            <span className="text-sm font-bold text-gray-700">用户名</span>
            <div className="relative mt-2">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                aria-label="用户名"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                className="w-full ios-input pl-12 pr-4 py-3.5 rounded-2xl"
                placeholder="用于登录的用户名"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-sm font-bold text-gray-700">邮箱</span>
            <div className="relative mt-2">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                aria-label="邮箱"
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full ios-input pl-12 pr-4 py-3.5 rounded-2xl"
                placeholder="接收邮箱验证码"
              />
            </div>
          </label>

          <label className="block">
            <span className="text-sm font-bold text-gray-700">邮箱验证码</span>
            <div className="flex gap-3 mt-2">
              <input
                aria-label="邮箱验证码"
                required
                value={verificationCode}
                onChange={(event) => setVerificationCode(event.target.value)}
                className="flex-1 min-w-0 ios-input px-4 py-3.5 rounded-2xl"
                placeholder="6 位验证码"
              />
              <button
                type="button"
                onClick={handleSendCode}
                disabled={sendingCode}
                className="px-4 rounded-2xl bg-gray-900 text-white text-sm font-bold disabled:opacity-60"
              >
                {sendingCode ? '发送中…' : '发送验证码'}
              </button>
            </div>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-bold text-gray-700">密码</span>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  aria-label="密码"
                  required
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full ios-input pl-12 pr-4 py-3.5 rounded-2xl"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-sm font-bold text-gray-700">确认密码</span>
              <input
                aria-label="确认密码"
                required
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full ios-input px-4 py-3.5 rounded-2xl mt-2"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-bold text-gray-700">邀请码</span>
            <div className="relative mt-2">
              <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                aria-label="邀请码"
                required
                value={invitationCode}
                onChange={(event) => setInvitationCode(event.target.value)}
                className="w-full ios-input pl-12 pr-4 py-3.5 rounded-2xl font-mono"
                placeholder="粘贴站长提供的邀请码"
              />
            </div>
          </label>

          {message && (
            <div
              className={`p-3 rounded-xl text-sm text-center font-bold ${
                isError ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'
              }`}
            >
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full ios-btn-primary h-14 rounded-2xl text-lg shadow-xl shadow-yellow-200 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                创建账号 <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
